import { Link } from "react-router-dom";
import { CheckCircle2, Star, Phone, MessageCircle, ArrowRight, Sparkles, ShieldCheck, Award, MapPin, IndianRupee, Briefcase, Quote, Send, Smartphone, Download, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import RegistrationForm from "../components/RegistrationForm";
import { TUTOR_JOBS, TUTOR_TESTIMONIALS } from "../data/tutorJobsData";
import { useSettings } from "../context/SettingsContext";

const SITE_URL = "https://anandtutorials.com";
const BOOKMYTUTOR_LOGO = "https://customer-assets.emergentagent.com/job_online-tuition-india/artifacts/tcrbh941_53909.png";
const BOOKMYTUTOR_DEFAULT_URL = "https://www.bookmytutor.com/download";

function StarsRow({ n }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`w-3.5 h-3.5 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            ))}
        </div>
    );
}

export default function TutorJobLanding({ slug }) {
    const data = TUTOR_JOBS[slug];
    const { settings } = useSettings();
    if (!data) return null;

    const phone = settings.head_office_phone || "+91 7000530316";
    const whatsapp = settings.whatsapp_number || "917000530316";
    const fullUrl = `${SITE_URL}${data.path}`;

    // ---------- JSON-LD: JobPosting (Google Jobs eligible) ----------
    const now = new Date();
    const validThrough = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000); // 90 days
    const orgRef = {
        "@type": "EducationalOrganization",
        "@id": `${SITE_URL}/#organization`,
        "name": "Anand Tutorials",
        "sameAs": SITE_URL,
        "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
    };
    const jobLocation = data.scope === "city" && data.cityName
        ? {
            "@type": "Place",
            "address": {
                "@type": "PostalAddress",
                "addressLocality": data.cityName,
                "addressRegion": data.cityRegion,
                "postalCode": data.cityPostalCode,
                "addressCountry": "IN",
            },
        }
        : {
            "@type": "Place",
            "address": {
                "@type": "PostalAddress",
                "addressCountry": "IN",
            },
        };
    const jobPostingSchema = {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        "title": data.h1,
        "description": `<p>${data.subTitle}</p><p>${data.pillars.slice(0, 3).map((p) => `<strong>${p.t}.</strong> ${p.d}`).join(" ")}</p>`,
        "datePosted": now.toISOString().slice(0, 10),
        "validThrough": validThrough.toISOString().slice(0, 10),
        "employmentType": data.employmentType,
        "hiringOrganization": orgRef,
        "jobLocation": jobLocation,
        ...(data.scope !== "city" && {
            "applicantLocationRequirements": { "@type": "Country", "name": "India" },
            "jobLocationType": data.intent === "tutor-jobs-online" ? "TELECOMMUTE" : undefined,
        }),
        "url": fullUrl,
        "directApply": true,
        "identifier": {
            "@type": "PropertyValue",
            "name": "Anand Tutorials Tutor Hiring",
            "value": slug,
        },
        "qualifications": "Minimum: 90+ board marks OR first-class graduation in the subject being taught. Police-checked. Subject-test cleared. 30-minute teaching demo passed.",
        "applicationContact": {
            "@type": "ContactPoint",
            "telephone": phone,
            "contactType": "Tutor Care",
        },
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": data.faqs.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    };

    const isCityScope = data.scope === "city";

    return (
        <div data-testid={`tutor-job-page-${slug}`} className="bg-white">
            <SEO
                title={data.seoTitle}
                description={data.seoDescription}
                keywords={data.keywords}
                path={data.path}
                schema={jobPostingSchema}
            />
            <SEO schema={faqSchema} />

            <Breadcrumbs items={[{ name: "Tutor Jobs", to: "/home-tutor-jobs-india" }, { name: data.cityName || data.h1.split("—")[0].trim() }]} />

            {/* Hero */}
            <PageHeader eyebrow={data.eyebrow} title={data.h1} subtitle={data.subTitle}>
                <div className="flex flex-wrap gap-3">
                    <Link to="/become-a-tutor" data-testid="tutor-cta-apply" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        Apply Now <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href={`tel:${phone.replace(/\s/g, "")}`} data-testid="tutor-cta-call" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/20 transition-colors">
                        <Phone className="w-4 h-4" /> Call Us
                    </a>
                    <a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi — interested in: ${data.h1}`)}`} target="_blank" rel="noopener noreferrer" data-testid="tutor-cta-whatsapp" className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-5 py-3 rounded-lg transition-colors">
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                </div>
            </PageHeader>

            {/* Stats */}
            <section className="py-10 bg-[#F8FAFC] border-b border-slate-100">
                <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {data.stats.map((s, i) => (
                        <div key={i} className="text-center">
                            <div className="font-heading font-bold text-xl sm:text-2xl md:text-3xl text-[#0F172A] break-words leading-tight">{s.v}</div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Pay disclosure ribbon */}
            <section className="py-6 bg-emerald-50 border-b border-emerald-100">
                <div className="max-w-6xl mx-auto px-6 lg:px-8 flex flex-wrap items-center gap-3 text-emerald-900 text-sm">
                    <IndianRupee className="w-5 h-5" />
                    <span className="font-semibold">How tutor engagements work:</span>
                    <span>Flexible earning opportunities · Earnings depend on class level, subjects, schedule and tutor profile · Personalised assignments matched to your expertise · Part-time &amp; full-time roles available · No fee collection at the door</span>
                </div>
            </section>

            {/* Pillars */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3 flex items-center gap-2">
                        <Sparkles className="w-3 h-3" /> Why teach here
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-10 max-w-3xl">
                        Six reasons {isCityScope ? `${data.cityName} tutors` : "tutors across India"} pick Anand Tutorials over gig platforms.
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {data.pillars.map((p, i) => (
                            <div key={i} className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6 hover:border-[#1E3A8A]/30 hover:shadow-md transition-all">
                                <div className="w-10 h-10 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center mb-4 font-heading font-bold">{i + 1}</div>
                                <h3 className="font-heading font-semibold text-[#0F172A] text-lg mb-2 leading-snug">{p.t}</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{p.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* EEAT */}
            <section className="py-16 sm:py-20 bg-[#0F172A] text-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300 mb-3 flex items-center gap-2">
                        <ShieldCheck className="w-3 h-3" /> Trust · Payment · Volume · Support
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-10 max-w-3xl">{data.eeatTitle}</h2>
                    <div className="grid md:grid-cols-2 gap-5">
                        {data.eeat.map((e, i) => (
                            <div key={i} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6">
                                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300 mb-2">{e.pillar}</div>
                                <p className="text-[15px] text-slate-200 leading-relaxed">{e.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Coverage */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3 flex items-center gap-2">
                        <Briefcase className="w-3 h-3" /> Assignment categories
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-10 max-w-3xl">What kinds of tuition jobs are available.</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.coverage.map((c, i) => (
                            <div key={i} className="bg-[#F8FAFC] border border-slate-200 rounded-xl p-5">
                                <div className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#1E3A8A]/5 text-[10px] font-bold uppercase tracking-wider text-[#1E3A8A] mb-2">{c.tag}</div>
                                <p className="text-sm text-slate-700 leading-relaxed">{c.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section className="py-16 sm:py-20 bg-[#F8FAFC]">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-600 mb-3">Side by side</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-10 max-w-3xl">Anand Tutorials vs typical tutor-job platforms.</h2>
                    <div className="overflow-x-auto bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-[#0F172A] text-white">
                                    <th className="px-4 py-3 text-left font-heading font-semibold"></th>
                                    <th className="px-4 py-3 text-left font-heading font-semibold">Anand Tutorials</th>
                                    <th className="px-4 py-3 text-left font-heading font-semibold">Typical alternative</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.compare.map((row, i) => (
                                    <tr key={i} className={i % 2 ? "bg-[#F8FAFC]" : ""}>
                                        <td className="px-4 py-3 font-semibold text-[#0F172A]">{row[0]}</td>
                                        <td className="px-4 py-3 text-slate-700">
                                            <span className="inline-flex items-center gap-1.5">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> {row[1]}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-500">{row[2]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Long-form deep SEO (optional) */}
            {Array.isArray(data.longFormSections) && data.longFormSections.length > 0 && (
                <section className="py-16 sm:py-20 bg-white">
                    <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-10">
                        {data.longFormSections.map((s, i) => (
                            <article key={i} data-testid={`tutor-longform-${i}`}>
                                <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4 text-[#0F172A]">{s.h2}</h2>
                                {s.paragraphs.map((p, j) => (
                                    <p key={j} className="text-slate-700 leading-relaxed text-base mb-4">{p}</p>
                                ))}
                            </article>
                        ))}
                    </div>
                </section>
            )}

            {/* City localities (city scope only) */}
            {isCityScope && data.cityLocalities?.length > 0 && (
                <section className="py-14 bg-[#F8FAFC] border-y border-slate-100">
                    <div className="max-w-6xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3 flex items-center gap-2">
                            <MapPin className="w-3 h-3" /> Localities with tutor demand
                        </div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-6">Home-tuition assignments across {data.cityName}.</h2>
                        <div className="flex flex-wrap gap-2">
                            {data.cityLocalities.map((loc) => (
                                <span key={loc} className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 font-semibold">{loc}</span>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Testimonials */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-4">
                            <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Tutor stories
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A]">From tutors on the panel right now.</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {TUTOR_TESTIMONIALS.map((t) => (
                            <div key={t.name} data-testid={`tutor-testimonial-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`} className="relative bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6">
                                <Quote className="absolute top-5 right-5 w-6 h-6 text-slate-200" />
                                <StarsRow n={t.rating} />
                                <p className="mt-4 text-[15px] text-slate-700 leading-relaxed">“{t.body}”</p>
                                <div className="mt-4 pt-4 border-t border-slate-100">
                                    <div className="font-heading font-semibold text-[#0F172A] text-sm">{t.name}</div>
                                    <div className="text-[11px] text-slate-500">{t.meta}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ + Application form */}
            <section className="py-16 sm:py-20 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14">
                    <div className="lg:col-span-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3B82F6] mb-3">Tutor FAQ</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6">Common questions from tutors.</h2>
                        <div className="space-y-3">
                            {data.faqs.map(([q, a], i) => (
                                <details key={q} data-testid={`tutor-faq-${i}`} className="group bg-white border border-slate-200 rounded-xl p-5 [&_summary::-webkit-details-marker]:hidden">
                                    <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] flex items-center justify-between gap-3">
                                        {q}
                                        <span className="text-[#1E3A8A] text-lg group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <div className="text-sm text-slate-600 mt-3 leading-relaxed">{a}</div>
                                </details>
                            ))}
                        </div>

                        {/* Strong CTA */}
                        <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300 mb-2">Ready to apply?</div>
                            <h3 className="font-heading text-xl font-bold mb-3">5-minute application. 5-7 day verification. Then live on the panel.</h3>
                            <div className="flex flex-wrap gap-3">
                                <Link to="/become-a-tutor" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-4 py-2.5 rounded-lg hover:bg-amber-400 transition-colors text-sm">
                                    <Send className="w-3.5 h-3.5" /> Apply Now
                                </Link>
                                <a href={`tel:${phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-semibold px-4 py-2.5 rounded-lg hover:bg-white/20 transition-colors text-sm">
                                    <Phone className="w-3.5 h-3.5" /> Call Tutor Care
                                </a>
                                <a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi — interested in: ${data.h1}`)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-4 py-2.5 rounded-lg text-sm">
                                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                </a>
                            </div>
                        </div>

                        {/* Related */}
                        <div className="mt-10 pt-8 border-t border-slate-200">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 mb-3 flex items-center gap-2">
                                <Award className="w-3 h-3" /> Related tutor-job pages
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {data.related.map((r) => (
                                    <Link
                                        key={r.to}
                                        to={r.to}
                                        data-testid={`tutor-related-${r.to.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
                                        className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:bg-[#1E3A8A] hover:text-white hover:border-[#1E3A8A] text-slate-700 font-semibold transition-colors"
                                    >
                                        {r.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="sticky top-24">
                            {data.applyVia === "bookmytutor-app" ? (
                                <BookMyTutorTutorApplyCta
                                    appUrl={settings.tutor_registration_external_url || BOOKMYTUTOR_DEFAULT_URL}
                                    cityName={data.cityName}
                                />
                            ) : (
                                <>
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-3">
                                        <Briefcase className="w-3 h-3" /> Quick application
                                    </div>
                                    <h3 className="font-heading text-xl font-bold text-[#0F172A] mb-1">Apply to teach</h3>
                                    <p className="text-xs text-slate-500 mb-4">Full form at <Link to="/become-a-tutor" className="text-[#1E3A8A] underline">/become-a-tutor</Link>. Or share basic info here and we&apos;ll call back.</p>
                                    <RegistrationForm />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

/**
 * V46 — Apply-as-Home-Tutor CTA card for the OFFLINE-only home-tutor-jobs
 * landing pages. Mirrors the BecomeATutor BookMyTutor App card (logo + QR +
 * Download button) but is purpose-built to convert recruiters to the
 * Book My Tutor App where the home-tutor application flow lives.
 */
function BookMyTutorTutorApplyCta({ appUrl, cityName }) {
    return (
        <div className="space-y-3" data-testid="apply-home-tutor-card">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-50 border border-rose-200 text-[10px] uppercase tracking-[0.22em] font-bold text-rose-700">
                <Briefcase className="w-3 h-3" /> Apply as Home Tutor
            </div>
            <div
                className="relative overflow-hidden bg-gradient-to-br from-[#E03A2F] via-[#E84A1B] to-[#F35F1A] text-white rounded-2xl shadow-xl p-7 flex flex-col items-center text-center"
                data-testid="apply-home-tutor-bookmytutor-card"
            >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full" />
                <div className="absolute -bottom-14 -left-8 w-40 h-40 bg-white/10 rounded-full" />

                <div className="relative w-52 h-20 rounded-2xl bg-white shadow-lg flex items-center justify-center overflow-hidden mb-4">
                    <img src={BOOKMYTUTOR_LOGO} alt="Book My Tutor app — Apply as a Home Tutor" className="w-full h-full object-contain scale-150" />
                </div>
                <h3 className="relative font-heading text-xl font-bold leading-tight mb-2">
                    Apply for Home Tutor Jobs{cityName ? ` in ${cityName}` : ""}
                </h3>
                <p className="relative text-sm text-white/95 mb-5">
                    All 1:1 offline home-tutor applications are accepted via the <strong>Book My Tutor App</strong>.
                </p>
                <a
                    href={appUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative inline-flex items-center justify-center gap-2 bg-white text-[#E03A2F] font-bold text-base px-6 py-3 rounded-full shadow-lg hover:scale-[1.02] active:scale-[0.98] transition w-full"
                    data-testid="apply-home-tutor-download-btn"
                >
                    <Download className="w-4 h-4" /> Download Book My Tutor App
                </a>

                <div className="relative mt-5 bg-white rounded-xl shadow-lg px-3 py-2.5 flex items-center gap-3 w-full">
                    <div className="bg-white rounded-md p-1 ring-1 ring-slate-200 shrink-0">
                        <QRCodeSVG value={appUrl} size={72} bgColor="#FFFFFF" fgColor="#0F172A" level="M" includeMargin={false} />
                    </div>
                    <div className="text-left">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#E03A2F]">
                            <QrCode className="w-3 h-3" /> Scan to install
                        </div>
                        <p className="text-[11px] text-slate-700 leading-snug mt-0.5">
                            Open your phone camera and scan to install the app.
                        </p>
                    </div>
                </div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[11px] text-amber-900 leading-relaxed flex items-start gap-2">
                <Smartphone className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                    <strong>Offline home tuition only.</strong> If you can only teach online, please apply through <Link to="/online-tutor-jobs" className="underline font-semibold">/online-tutor-jobs</Link> instead — we already have enough online tutors.
                </span>
            </div>
        </div>
    );
}
